<template>
  <div class="d-flex justify-center align-center pa-0 h-100">
    <div v-if="!githubRepo" class="d-flex ga-2">
      <div class="d-flex align-center justify-center flex-column my-10 text-center">
        <div class="d-flex justify-center align-center mb-4">
          <img src="/favicon.svg" width="48" height="48" class="mr-2" />
          <v-icon size="48">mdi-swap-horizontal</v-icon>
          <v-icon size="48" class="ml-2">mdi-github</v-icon>
        </div>
        <p class="text-h5 mb-3">Connect to GitHub</p>
        <v-select
          v-model="selectedAccount"
          :items="githubAccounts"
          label="Select GitHub Account"
          item-title="login"
          item-value="login"
          variant="outlined"
          density="comfortable"
          style="width: 420px"
          return-object
          @update:model-value="handleAccountChange"
        >
          <template #selection="{ item }">
            <template v-if="item && !item.raw.isAddNew">
              <v-avatar size="24" class="mr-2">
                <img :src="item.raw.avatar" :alt="item.raw.login" class="w-100 h-100" />
              </v-avatar>
              <span>{{ item.raw.login }}</span>
            </template>
            <template v-else>
              <v-icon class="mr-2">mdi-plus</v-icon>
              <span>Add another account</span>
            </template>
          </template>
          <template #item="{ item, props }">
            <template v-if="!item.raw.isAddNew">
              <div v-bind="props" class="d-flex align-center px-2 py-3 cursor-pointer">
                <v-avatar size="24" class="mr-2">
                  <img :src="item.raw.avatar" :alt="item.raw.login" class="w-100 h-100" />
                </v-avatar>
                <span>{{ item.raw.login }}</span>
              </div>
            </template>
            <template v-else>
              <div v-bind="props" class="d-flex align-center px-2 py-3 cursor-pointer">
                <v-icon class="mr-2">mdi-plus</v-icon>
                <span>Add another account</span>
              </div>
            </template>
          </template>
        </v-select>
        <v-list class="border" style="width: 420px;height: 240px;">
          <v-list-item
            v-for="repo in repos"
            :key="repo.full_name"
            :active="selectedRepo && selectedRepo.full_name === repo.full_name"
            @click="selectedRepo = repo"
            class="cursor-pointer"
          >
            <template v-slot:prepend>
              <v-icon icon="mdi-source-repository"></v-icon>
            </template>
            <v-list-item-title>{{ repo.name }}</v-list-item-title>
            <v-list-item-subtitle v-if="repo.description">
              {{ repo.description }}
            </v-list-item-subtitle>
            <template v-slot:append>
              <v-icon :icon="repo.private ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline'" color="grey" />
            </template>
          </v-list-item>
        </v-list>
        <v-btn-group
          variant="outlined"
          block
          style="width: 420px;"
        >
          <v-btn
            class="w-50" style="border-radius: 0 !important;"
            :disabled="!selectedAccount"
            @click="isModalOpened = true"
          >
            <v-icon class="mr-2">mdi-plus</v-icon> Create Repository
          </v-btn>
          <v-divider vertical />
          <v-btn
            class="w-50" style="border-radius: 0 !important;"
            :disabled="!selectedAccount || !selectedRepo"
            @click="() => linkGitHubRepo(selectedAccount.login, selectedRepo.owner.login, selectedRepo.name)"
          >
            <v-icon class="mr-2">mdi-link</v-icon> Link Repository
          </v-btn>
        </v-btn-group>
      </div>
    </div>
    <v-row v-else class="ma-0 h-100">
      <v-col sm="3" class="pa-0">
        <div class="d-flex flex-column ga-2 pa-2">
          <div class="d-flex">
            <span>Source Control</span>
            <v-spacer></v-spacer>
            <v-btn @click="doCommit()" :disabled="!Object.keys(diffObjects).length || !commitMessage.length" size="small" icon="mdi-check"></v-btn>
            <v-btn @click="refreshGithub()" size="small" icon="mdi-refresh"></v-btn>
            <v-menu>
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  size="small"
                  icon
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item @click="unlinkGitHub">
                  <v-list-item-title>Unlink GitHub</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <v-text-field
            v-model="commitMessage"
            variant="outlined"
            density="compact"
            single-line
            hide-details
          />
          <v-btn @click="doCommit()" :disabled="!Object.keys(diffObjects).length || !commitMessage.length">
            <v-progress-circular v-if="isCommitting" indeterminate size="16" width="2" class="mr-1" />
            <v-icon v-else>mdi-check</v-icon> Commit & Push
          </v-btn>
        </div>
        <v-divider />
        <v-list
          style="height: 636px"
          bg-color="transparent"
          density="compact"
          open-strategy="multiple"
          v-model:selected="tabs"
          v-model:opened="opened"
        >
          <v-list-item
            v-for="(object, filepath) in diffObjects"
            :key="filepath"
            prepend-icon="mdi-file"
            :title="filepath.split('/').pop()"
            :value="filepath"
            :class="object.originalContents === null ? 'text-decoration-line-through' : ''"
          />
        </v-list>
        <v-divider />
        <div class="pa-2">
          <v-select
            :model-value="githubBranch"
            :items="branches"
            label="Select GitHub Branch"
            item-title="name"
            item-value="name"
            variant="outlined"
            density="compact"
            style="width: 100%"
            return-object
            @update:model-value="handleBranchChange"
          >
            <!-- <template #selection="{ item }">
              <v-avatar size="24" class="mr-2">
                <img :src="item.raw.avatar" :alt="item.raw.owner" class="w-100 h-100" />
              </v-avatar>
              <span>{{ item.raw.owner }}</span>
            </template>
            <template #item="{ item, props }">
              <div v-bind="props" class="d-flex align-center px-2 py-3 cursor-pointer">
                <v-avatar size="24" class="mr-2">
                  <img :src="item.raw.avatar" :alt="item.raw.owner" class="w-100 h-100" />
                </v-avatar>
                <span>{{ item.raw.owner }}</span>
              </div>
            </template> -->
          </v-select>
        </div>
      </v-col>
      <v-col sm="9" class="pa-0" style="background-color: white; border-left: 1px solid rgb(230, 230, 230);">
        <template v-for="(object, filename) in diffObjects" :key="filename">
          <div v-if="openedTab === filename" style="position: relative;">
            <div class="d-flex" style="border-bottom: 1px solid #ddd;">
              <span class="toolbar-top pl-3 pt-3">
                {{ object.filepath.replaceAll('/', ' > ')}}
                {{ object.originalContents === null ? '(deleted)' : '' }}
              </span>
              <v-spacer></v-spacer>
              <v-tooltip location="top" open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    style="border-radius: 0px !important;"
                    variant="text"
                    density="compact"
                    class="text-none"
                    :disabled="!object.isDirty || $store.state.isWorkflowDisabled"
                    @click="saveContent(object)"
                  >
                    <v-icon class="mr-2">mdi-content-save</v-icon> Save
                  </v-btn>
                </template>
                <span>Save changes Ctrl/Cmd+S</span>
              </v-tooltip>
            </div>
            <code-diff-viewer
              v-if="!!object.originalContents"
              v-model:original="object.githubContents"
              v-model="object.userUpdatedContents"
              v-model:isDirty="object.isDirty"
              @save="saveContent(object)"
              :language="getFileExt(object.filepath)"
            />
            <code-editor
              v-else
              v-model="object.contents"
              v-model:isDirty="object.isDirty"
              @save="saveFile(object)"
              :language="getFileExt(object.filepath)"
            />
          </div>
        </template>
      </v-col>
      <v-dialog v-model="askBeforeSaveContent" max-width="480">
        <v-card title="Warning">
          <v-card-text>
            AI-generated changes were applied based on recent updates to the domain model. <br />
            Your own edits may conflict with these changes. How would you like to proceed?
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn @click="handleMerge">Merge</v-btn>
            <v-btn @click="handleReplace">Replace</v-btn>
            <v-btn @click="handleCancel">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
    <v-dialog v-model="isModalOpened" max-width="500">
      <v-card title="Create Repository">
        <v-card-text>
          <v-text-field
            v-model="newRepo.name"
            label="Repository Name"
            variant="outlined"
            density="compact"
            autofocus
          />
          <div v-if="normalizedRepoName !== trimmedRepoName" class="repo-hint">
            <v-icon
              color="success"
              size="18"
              style="margin-right: 4px"
            >
              mdi-check-circle
            </v-icon>
            <div>
              <span>Your new repository will be created as {{normalizedRepoName}}.</span><br/>
              <span>The repository name can only contain ASCII letters, digits, and the characters ., -, and _.</span>
            </div>
          </div>
          <v-textarea
            v-model="newRepo.description"
            label="Description"
            rows="2"
            variant="outlined"
            density="compact"
          />
          <v-radio-group v-model="newRepo.isPrivate" row>
            <v-radio label="Public" :value="false" />
            <v-radio label="Private" :value="true" />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="isModalOpened = false">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="createGitHubRepo(selectedAccount.login, normalizedRepoName, newRepo.description, newRepo.isPrivate); isModalOpened = false"
            :disabled="!newRepo.name"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CodeDiffViewer from '@/components/CodeDiffViewer.vue'
import CodeEditor from '@/components/CodeEditor.vue'
import { getLanguageFromFilename } from '@/codegen/utils'
import { merge } from 'node-diff3'
// import _ from 'lodash'
import gitHubApi from '@/api/gitHubApi'

export default {
  components: {
    CodeDiffViewer,
    CodeEditor
  },
  props: [
    'repos',
    'branches',
    'githubRepo',
    'githubBranch',
    'fileObjects',
    'diffObjects',
    'createGitHubRepo',
    'listGitHubRepos',
    'linkGitHubRepo',
    'makeCommit',
    'refreshGithub',
    'unlinkGitHub',
    'saveFile'
  ],
  data () {
    return {
      tabs: [],
      opened: [],
      openedTab: '',
      newRepoName: '',
      linkedRepo: null,
      commitMessage: '',
      isCommitting: false,
      askBeforeSaveContent: false,
      selectedObject: {},
      prevSelectedAccount: null,
      selectedAccount: null,
      selectedRepo: null,

      // Create New repository
      isModalOpened: false,
      newRepo: {
        name: '',
        description: '',
        isPrivate: false
      }
    }
  },
  computed: {
    ...mapState({
      connectedGithubAccounts: state => state.github.githubAccounts
    }),
    githubAccounts () {
      return [
        ...(this.connectedGithubAccounts || []),
        { isAddNew: true }
      ]
    },
    normalizedRepoName () {
      return this.newRepo.name.trim().replace(/\s+/g, '-')
    },
    trimmedRepoName () {
      return this.newRepo.name.trim()
    }
  },
  watch: {
    tabs (val) {
      if (val[0]) {
        this.openedTab = val[0]
      } else {
        this.tabs[0] = this.openedTab
      }
    }
  },
  async mounted () {
    await this.refreshGithub()
  },
  methods: {
    async connectGitHub () {
      console.log('Connect to Github')
      const clientId = process.env.VUE_APP_GITHUB_CLIENT_ID
      const currentOrigin = window.location.origin
      const redirectUri = encodeURIComponent(`${currentOrigin}/github/callback`)
      const scope = 'repo'
      const authUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}`

      window.open(authUrl, '_blank', 'width=600,height=700')

      window.addEventListener('message', async (event) => {
        if (event.origin !== currentOrigin) return // adjust to your domain
        if (event.data.type === 'github-auth') {
          const authCode = event.data.code

          const response = await gitHubApi.saveGitHubToken(authCode)

          if (response.success) {
            console.log('Got GitHub auth result:', response.resData)
          } else {
            this.$store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
          }
        }
      })
    },
    // async listGitHubRepoFiles () {
    //   const resData = await gitHubApi.listGitHubRepoFiles('calimameh', 'calimameh', 'Todo8', '', 'main')
    //   console.log(resData)
    // },
    // async commitGitHubRepoFiles () {
    //   const files = [
    //     { type: 'added', path: '1.txt', content: '1.txt 1.txt' },
    //     { type: 'deleted', path: '.gitkeep' }
    //   ]

    //   const resData = await gitHubApi.commitGitHubRepoFiles('calimameh', 'calimameh', 'test2025', 'main', files, 'test commit')
    //   console.log(resData)
    // },
    // async deleteGitHubRepo () {
    //   const resData = await gitHubApi.deleteGitHubRepo('calimameh', 'calimameh', 'test2025')
    //   console.log(resData)
    // },
    getFileExt (filePath) {
      return getLanguageFromFilename(filePath)
    },
    async doCommit () {
      if (this.isCommitting) return

      this.isCommitting = true
      await this.makeCommit(this.commitMessage)
      this.commitMessage = ''
      this.isCommitting = false
    },
    async saveContent (object, saveOption = '') {
      this.selectedObject = object

      if (object.aiUpdatedContents !== null) {
        this.askBeforeSaveContent = true
      } else {
        await this.handleReplace()
      }
    },
    async handleMerge () {
      if (this.openedTab === '') return

      console.log(this.selectedObject)

      const baseLines = this.selectedObject.originalContents.split('\n')
      const aiUpdate = this.selectedObject.aiUpdatedContents.split('\n')
      const manualUpdate = this.selectedObject.userUpdatedContents.split('\n')

      const mergedResult = merge(aiUpdate, baseLines, manualUpdate)
      const hasConflict = mergedResult.conflict
      const mergedContents = mergedResult.result.join('\n')

      console.log(mergedResult)

      if (hasConflict) {
        this.$store.commit('snackbar/showMessage', { content: 'Merge conflict detected. Please resolve manually.', timeout: 3000, color: 'red' }, { root: true })
      } else {
        this.$store.commit('snackbar/showMessage', { content: 'User and AI updates merged successfully.', timeout: 3000, color: 'green' }, { root: true })
      }
      this.$emit('update:diffObjects', {
        ...this.diffObjects,
        [this.openedTab]: {
          ...this.diffObjects[this.openedTab],
          userUpdatedContents: mergedContents,
          aiUpdatedContents: null,
          isDirty: false
        }
      })
      const updatedFileObject = {
        ...this.fileObjects[this.openedTab],
        contents: mergedContents,
        draftContents: null,
        isDirty: false
      }
      await this.saveFile(updatedFileObject)
      await this.handleCancel()
    },
    async handleReplace () {
      this.$emit('update:diffObjects', {
        ...this.diffObjects,
        [this.openedTab]: {
          ...this.diffObjects[this.openedTab],
          originalContents: this.selectedObject.userUpdatedContents,
          userUpdatedContents: this.selectedObject.userUpdatedContents,
          aiUpdatedContents: null,
          isDirty: false
        }
      })

      const updatedFileObject = {
        ...this.fileObjects[this.openedTab],
        contents: this.selectedObject.userUpdatedContents,
        draftContents: null,
        isDirty: false
      }
      await this.saveFile(updatedFileObject)
      await this.handleCancel()
    },
    async handleCancel () {
      this.askBeforeSaveContent = false
      this.selectedObject = {}
    },
    async handleAccountChange (account) {
      if (account && account.isAddNew) {
        this.selectedAccount = this.prevSelectedAccount
        await this.connectGitHub()
      } else {
        this.prevSelectedAccount = account
        await this.listGitHubRepos(account.login)
      }
    },
    async handleBranchChange (branch) {
      await this.$emit('update:githubBranch', branch)
    }
  }
}
</script>

<style scoped>
.repo-hint {
  margin-top: -18px;
  margin-bottom: 4px;
  font-size: 0.8rem;        /* Small, subtle text */
  display: flex;            /* Align icon and text horizontally */
  align-items: center;      /* Vertically center icon and text */
  line-height: 1.4;         /* Improve readability for multi-line text */
  white-space: pre-line;    /* Allow \n in the string to break lines */
}

.repo-hint span:first-child {
  color: #1a7f37;
}

.repo-hint .v-icon {
  margin-right: 4px;        /* Space between icon and text */
}
</style>
